import { faCirclePlus, faBars } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, ClickAwayListener, Divider, IconButton, MenuItem, MenuList, Paper, Popper, Tooltip } from '@mui/material';
import MuiToolbar from '@mui/material/Toolbar';
import * as React from 'react';
import { useState } from 'react';
import { useFeatureToggles } from '../../feature-toggles';
import { useTenantConfiguration } from '../../hooks/use-tenant-configuration';
import { useTenantAddons } from '../tenant-addons';
import { useAllowedActions } from '../../authentication/use-allowed-actions';
import { IAction, ISelectedEntity } from './actions/interfaces';
import { getActions, getUniverseActions } from './getActions';
import { nudgeInForumActionId } from './actions/citizens';
import { useCanWriteCitizenDialog } from '../../areas/forum/caseworker/citizen-dialog-access';
import './toolbar.scss';
// Actions without url or function is in dashboard-worklist-component.tsx and the modal is located here bulk-action-modal.tsx
// When the old toolbar is removed the components in bulk-action-modal.tsx shold be moved too

export interface IUniToolbarProps {
  selectedEntities: ISelectedEntity[];
  rowCount: number;
  context: string;
  universe?: string;
  onButtonClick?: (action: IAction, event: React.MouseEvent<HTMLButtonElement, MouseEvent>, updateButtonState: (item: IAction, state: ButtonState) => void) => void;
  title: string | JSX.Element;
  isFiltered?: boolean;
  forceVisibility?: (id: string) => boolean | undefined;
  hideActionsById?: string[];
  onAddButtonClick?: () => void;
  customTools?: React.ReactNode;
  isLoading?: boolean;
  onActionCompleted?: (keepSelection: boolean) => void;
}

export enum ButtonState {
  none,
  disabled,
}
export function UniToolbar(props: IUniToolbarProps) {
  const {
    selectedEntities,
    context,
    onButtonClick,
    title,
    rowCount,
    isFiltered,
    forceVisibility,
    hideActionsById,
    universe,
    onAddButtonClick,
    customTools,
    isLoading,
    onActionCompleted,
  } = props;

  const { isFeatureEnabled } = useFeatureToggles();
  const { isTenantAddonEnabled } = useTenantAddons();

  const [Modal, setModal] = useState<JSX.Element>();
  const { tenantName, crmUrl, caseWeb, organizationCode } = useTenantConfiguration();
  const canWriteCitizenDialog = useCanWriteCitizenDialog();
  const hiddenActionIds = canWriteCitizenDialog ? hideActionsById || [] : [...(hideActionsById || []), nudgeInForumActionId];
  const actions = getActions(context, selectedEntities.length, isFeatureEnabled, isTenantAddonEnabled, hiddenActionIds, forceVisibility);
  const allowedActions = useAllowedActions();
  const universeActions = getUniverseActions(context, selectedEntities.length, isFeatureEnabled, isTenantAddonEnabled, hideActionsById || [], forceVisibility, universe).filter(
    isAccessible
  );

  const [isRootEntityMenuOpen, setIsRootEntityMenuOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const [buttonStates, setButtonStates] = React.useState<{ [id: string]: ButtonState }>({});

  function getUniverseName(itemsCount: number) {
    if (universe?.toLocaleLowerCase() === 'citizen') {
      return itemsCount > 1 ? 'Borgere' : 'Borger';
    }
    if (universe?.toLocaleLowerCase() === 'company') {
      return itemsCount > 1 ? 'Virksomheder' : 'Virksomhed';
    }
    return 'Unknown-Universe';
  }
  async function handleClick(item: IAction, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    const isRootEntityAction = universe && context !== universe && (item.showOnUniverseAction || item.contextOnlyAction);
    let selectedIds = [];
    let selectedRootIds: string[] = [];
    if (isRootEntityAction) {
      const entries = selectedEntities.map(x => (isRootEntityAction ? x.rootEntityId || '' : x.entityId));
      selectedIds = Array.from(new Set(entries));
      selectedRootIds = selectedIds;
    } else {
      selectedIds = selectedEntities.map(x => x.entityId);
      selectedRootIds = selectedEntities.filter(x => x.rootEntityId).map(x => x.rootEntityId) as string[];
    }
    const rootIds = Array.from(new Set(selectedRootIds));
    if (item.action instanceof Function) {
      const result = await item.action(selectedIds, rootIds, isFeatureEnabled, replaceUrl, selectedEntities, () =>
        onActionCompleted?.(!!item.keepsSelectionWhenCompleted)
      );
      if (result) {
        // we have a modal to show, make sure its not reused
        const elem = <span key={Math.random()}>{result}</span>;
        setModal(elem);
      }
    } else if (typeof item.action === 'string') {
      window.open(replaceUrl(item.action), item.openInSameWindow ? '' : '_blank');
    }
    setIsRootEntityMenuOpen(false);
    onButtonClick && onButtonClick(item, event, setButtonState);
  }

  function replaceUrl(uri: string) {
    let url = uri;
    const muni = organizationCode?.substring(0, 3);
    if (crmUrl) {
      url = url.replaceAll('{crmHost}', crmUrl);
    }
    if (tenantName) {
      url = url.replaceAll('{tenant}', tenantName);
    }
    if (caseWeb && muni) {
      url = url.replaceAll('{schultzSagHost}', caseWeb).replaceAll('{muni}', muni);
    }
    return url;
  }

  function setButtonState(item: IAction, state: ButtonState) {
    buttonStates[item.id] = state;
    setButtonStates({ ...buttonStates });
  }

  function isAccessible(item: IAction) {
    return !item.requiredAccess || allowedActions.isAnyAllowed(item.requiredAccess);
  }

  function isDisabled(item: IAction) {
    const buttonStatesDisabled = buttonStates[item.id] === ButtonState.disabled;
    const actionDisabled = item.isDisabled && !!item.isDisabled(selectedEntities.length, rowCount, !!isLoading, selectedEntities);
    return buttonStatesDisabled || actionDisabled;
  }

  function getTooltip(item: IAction) {
    const actionDisabled = item.isDisabled && item.isDisabled(selectedEntities.length, rowCount, !!isLoading, selectedEntities);
    return actionDisabled || item.displayName;
  }

  function getIcon(item: IAction) {
    if (item.icon instanceof Function) {
      return item.icon(isFiltered);
    }
    return item.icon;
  }
  function getUniqueSelectedEntities() {
    return Array.from(new Set(selectedEntities.map(x => x.rootEntityId))).length;
  }
  const bottomUniverseActions = universeActions.filter(x => x.showInBottomMenuSection);
  const contextOnlyUniverseActions = universeActions.filter(x => x.contextOnlyAction && !x.showInBottomMenuSection);
  const otherUniverseActions = universeActions.filter(x => !x.contextOnlyAction && !x.showInBottomMenuSection);

  function renderUniverseMenuItem(a: IAction) {
    return (
      <Tooltip style={{ cursor: 'pointer' }} key={a.displayName} title={getTooltip(a)} placement="left">
        <span>
          <MenuItem onClick={(event: any) => handleClick(a, event)} disabled={!!isDisabled(a)}>
            <FontAwesomeIcon icon={getIcon(a)} size="xl" style={{ marginRight: '7px', width: '19px', height: '19px', fontSize: '16px' }} />
            <span style={{ fontSize: '14px' }}> {a.displayName}</span>
          </MenuItem>
        </span>
      </Tooltip>
    );
  }

  function shouldShowUniverseButton() {
    return selectedEntities.length > 0 && context !== universe && universeActions.length > 0;
  }

  // toggle UI extracted to dashboard area and injected via additionalElementsBeforeActions

  return (
    <MuiToolbar
      variant="dense"
      className={selectedEntities.length > 0 ? 'toolbar toolbar--withSelectedCheckboxes' : 'toolbar'}
      data-vanguard="table-toolbar"
      sx={{
        border: 'solid 1px rgba(0, 0, 0, 0.15)',
        borderBottom: 'none',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'Roboto', fontWeight: 500, fontSize: '0.9em', margin: -0.54 }}>
        {onAddButtonClick && (
          <Tooltip title="Ny arbejdsliste">
            <IconButton onClick={() => onAddButtonClick && onAddButtonClick()} size="small">
              <FontAwesomeIcon icon={faCirclePlus} color="rgba(0, 0, 0, 0.75)" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ lineHeight: '20px' }}>{title}</Box>
      </Box>
      <div className="toolbar-spacer" />
      <div className="toolbar-tools">
        {customTools}
        {shouldShowUniverseButton() && (
          <Popper style={{ zIndex: 1000 }} open={isRootEntityMenuOpen} anchorEl={anchorRef.current} role={undefined} placement="bottom-start">
            <Paper>
              <ClickAwayListener onClickAway={() => setIsRootEntityMenuOpen(false)}>
                <MenuList id="composition-menu" aria-labelledby="composition-button">
                  {contextOnlyUniverseActions.map(renderUniverseMenuItem)}
                  {contextOnlyUniverseActions.length > 0 && <Divider orientation="horizontal" />}
                  {otherUniverseActions.map(renderUniverseMenuItem)}
                  {bottomUniverseActions.length > 0 && <Divider orientation="horizontal" />}
                  {bottomUniverseActions.map(renderUniverseMenuItem)}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
        )}
        {shouldShowUniverseButton() && (
          <Button startIcon={<FontAwesomeIcon icon={faBars} />} ref={anchorRef} onClick={() => setIsRootEntityMenuOpen(!isRootEntityMenuOpen)}>
            {getUniqueSelectedEntities()} {getUniverseName(getUniqueSelectedEntities())}
          </Button>
        )}
        {shouldShowUniverseButton() && selectedEntities.length > 0 && <Divider orientation="vertical" variant="middle" flexItem />}
        {actions.map(a => (
          <Tooltip style={{ cursor: 'pointer' }} key={a.displayName} title={getTooltip(a)}>
            <span>
              <IconButton
                style={{ color: a.iconColor && a.iconColor(isFiltered) }}
                size="large"
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleClick(a, event)}
                disabled={!!isDisabled(a)}
              >
                <FontAwesomeIcon icon={getIcon(a)} size="xs" style={{ fontSize: '16px' }} />
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </div>
      {Modal && Modal}
    </MuiToolbar>
  );
}
